import { extractMCPErrorMessage } from '@littlebird/mcp';
import { useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Tooltip } from '@/components/ui/Tooltip';
import { integrationStatusConfig } from '@/features/integrations/integration-status-ui';
import { cn } from '@/lib/utils';

import { useProviderAccountActions } from './integration-management.actions';

import type {
  IntegrationAccount,
  IntegrationProvider,
} from '@littlebird/integrations';

type ActionStatus = 'idle' | 'pending' | 'error';

// Same catch-and-surface pattern RemoveControl pioneered: an action stays
// optimism-free (no local flip of the row's own data) and just tracks
// whether its own last attempt is in flight or failed. A later successful
// attempt clears the error rather than leaving it stuck.
function useActionStatus() {
  const [status, setStatus] = useState<ActionStatus>('idle');
  const [error, setError] = useState<unknown>(null);

  const run = (action: () => Promise<void>) => {
    setStatus('pending');
    setError(null);
    action().then(
      () => setStatus('idle'),
      (err: unknown) => {
        setError(err);
        setStatus('error');
      }
    );
  };

  return { status, error, run };
}

// Only backend responses carry a detail worth showing; a bare Error's message
// is an internal string ("failed", "Network Error") that adds nothing.
function isHttpError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    ('status' in error || 'body' in error)
  );
}

function accountDisplayStatus(account: IntegrationAccount) {
  return account.enabled ? account.status : 'disabled';
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A label or identity hint is sometimes just the account's own row id (some
// MCP servers default account_identity_hint to it when there's nothing more
// meaningful to report) — never surface that back to the user as if it were
// a human-readable name.
function looksLikeRawId(value: string, id: string): boolean {
  return value === id || UUID_PATTERN.test(value);
}

function accountEndpointHost(endpoint: string | null): string | null {
  if (!endpoint) return null;
  try {
    return new URL(endpoint).host;
  } catch {
    return null;
  }
}

/** label -> a human identity hint -> the provider's own name -> a generic
 * fallback with the endpoint host. Never falls through to the raw account id. */
function accountName(
  account: IntegrationAccount,
  providerName: string
): string {
  if (account.label && !looksLikeRawId(account.label, account.id)) {
    return account.label;
  }
  if (
    account.identityHint &&
    !looksLikeRawId(account.identityHint, account.id)
  ) {
    return account.identityHint;
  }
  if (providerName) return providerName;
  const host = accountEndpointHost(account.endpoint);
  return host ? `Account (${host})` : 'Account';
}

type AttentionStatus = Extract<
  IntegrationAccount['status'],
  'expired' | 'cancelled' | 'failed' | 'unauthorized' | 'unreachable'
>;

interface AttentionSpec {
  tint: 'amber' | 'red';
  icon: typeof Icons.warn;
  message: string;
  ctaLabel: string;
  action: 'reconnect' | 'recheck';
}

// One state-matched CTA per attention status. `pending`/`probing` are
// connecting, not an error, so they're absent here on purpose — no strip.
const ATTENTION_SPECS: Record<AttentionStatus, AttentionSpec> = {
  expired: {
    tint: 'amber',
    icon: Icons.warn,
    message:
      "You've been signed out. Sign in again to keep using this account.",
    ctaLabel: 'Sign in again',
    action: 'reconnect',
  },
  cancelled: {
    tint: 'amber',
    icon: Icons.warn,
    message: 'Setup stopped halfway. Finish it to start using this account.',
    ctaLabel: 'Finish connecting',
    action: 'reconnect',
  },
  failed: {
    tint: 'red',
    icon: Icons.warn,
    message: 'This account stopped working. Signing in again usually fixes it.',
    ctaLabel: 'Sign in again',
    action: 'reconnect',
  },
  unauthorized: {
    tint: 'red',
    icon: Icons.lock,
    message: 'The key used to connect this account no longer works.',
    ctaLabel: 'Update key',
    action: 'reconnect',
  },
  unreachable: {
    tint: 'red',
    icon: Icons.plugZap,
    message: "This account isn't responding right now.",
    ctaLabel: 'Check again',
    action: 'recheck',
  },
};

function isAttentionStatus(
  status: IntegrationAccount['status']
): status is AttentionStatus {
  return status in ATTENTION_SPECS;
}

// A paused (disabled) or still-connecting account isn't in an error state —
// only an enabled account whose status maps to ATTENTION_SPECS gets a strip.
function attentionSpecFor(account: IntegrationAccount): AttentionSpec | null {
  return account.enabled && isAttentionStatus(account.status)
    ? ATTENTION_SPECS[account.status]
    : null;
}

// The strip stays on the card's own background — severity shows only
// through the icon color, per Shivam's call (a filled warning band read
// as too loud inside the tile).
const STRIP_ICON_CLASSES: Record<AttentionSpec['tint'], string> = {
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
};

/** Small "i" pill next to the account name — replaces the always-visible MCP
 * endpoint line. Only MCP accounts with an endpoint have detail worth
 * showing; the endpoint (and a human identity hint, if any) surface on
 * hover instead of taking up permanent row space. */
function AccountInfoPill({
  account,
  name,
}: {
  account: IntegrationAccount;
  name: string;
}) {
  if (account.source !== 'mcp' || !account.endpoint) return null;

  // Same condition as AccountRow's identityLine — when the identity hint is
  // what's standing in for a missing label, it's already the row's name;
  // repeating it in the tooltip would just duplicate it.
  const identityHint =
    account.identityHint &&
    account.identityHint !== name &&
    !looksLikeRawId(account.identityHint, account.id)
      ? account.identityHint
      : undefined;

  return (
    <Tooltip label={account.endpoint} description={identityHint} side="top">
      <span
        aria-label={`Connection details: ${account.endpoint}`}
        className="inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-border-strong text-foreground-subtle"
      >
        <Icons.info className="size-2.5" />
      </span>
    </Tooltip>
  );
}

function StatusBadge({ account }: { account: IntegrationAccount }) {
  const status = accountDisplayStatus(account);
  if (status === 'disconnected') return null;
  const config = integrationStatusConfig[status];
  return (
    <span
      className={cn(
        'text-xs rounded-full px-2 py-0.5 shrink-0',
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

function RenameControl({
  account,
  name,
  onRename,
}: {
  account: IntegrationAccount;
  name: string;
  onRename: (label: string) => Promise<void>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(account.label);
  const cancelRef = useRef(false);
  const { status, run } = useActionStatus();

  // Keeps the editor's draft synced with a rename that lands from elsewhere
  // (or is rolled back) while this row isn't being actively edited.
  useEffect(() => {
    if (!isEditing) setValue(account.label);
  }, [account.label, isEditing]);

  const commit = () => {
    setIsEditing(false);
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    const trimmed = value.trim();
    if (trimmed && trimmed !== account.label) {
      run(() => onRename(trimmed));
    }
  };

  if (isEditing) {
    return (
      <div className="w-48 shrink-0">
        <Input
          autoFocus
          variant="onSurface"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              cancelRef.current = true;
              e.currentTarget.blur();
            }
          }}
          className="h-7 text-sm"
        />
      </div>
    );
  }

  return (
    <YStack className="gap-0.5">
      <XStack className="group/rename min-w-0 items-center gap-1.5">
        <button
          type="button"
          aria-label={`Rename ${name}`}
          disabled={status === 'pending'}
          onClick={() => {
            setValue(account.label);
            setIsEditing(true);
          }}
          className="min-w-0 truncate text-left text-sm font-medium text-foreground disabled:opacity-50"
        >
          {name}
        </button>
        <AccountInfoPill account={account} name={name} />
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          disabled={status === 'pending'}
          onClick={() => {
            setValue(account.label);
            setIsEditing(true);
          }}
          className="shrink-0 opacity-0 touch:opacity-100 transition-opacity group-hover/rename:opacity-100"
        >
          <Icons.pencil className="size-3 text-foreground-muted" />
        </button>
      </XStack>
      {status === 'error' && (
        <p className="text-xs text-destructive">Failed to rename account</p>
      )}
    </YStack>
  );
}

function AccountAttentionStrip({
  account,
  name,
  onReconnect,
  onRecheck,
}: {
  account: IntegrationAccount;
  name: string;
  onReconnect: () => Promise<void>;
  onRecheck: () => Promise<void>;
}) {
  const { status, error, run } = useActionStatus();
  const spec = attentionSpecFor(account);
  if (!spec) return null;
  const failure = `Failed to ${spec.ctaLabel.toLowerCase()}`;
  // The backend's detail is the only thing that distinguishes "provider moved
  // its auth server" from "network blip" - without it every failure reads the
  // same and is undiagnosable from the UI.
  const detail =
    status === 'error' && isHttpError(error)
      ? extractMCPErrorMessage(error, '')
      : '';
  const Icon = spec.icon;

  return (
    <XStack className="items-center gap-2 px-4 pb-3">
      <Icon className={cn('size-4 shrink-0', STRIP_ICON_CLASSES[spec.tint])} />
      <p className="min-w-0 truncate text-xs text-foreground">
        {spec.message}{' '}
        <button
          type="button"
          aria-label={`${spec.ctaLabel} ${name}`}
          disabled={status === 'pending'}
          onClick={() =>
            run(spec.action === 'reconnect' ? onReconnect : onRecheck)
          }
          className="cursor-pointer font-medium underline underline-offset-2 transition-colors hover:text-foreground-muted disabled:opacity-50"
        >
          {spec.ctaLabel}
        </button>
      </p>
      {status === 'error' && (
        <span
          className="min-w-0 shrink truncate text-xs text-destructive"
          title={detail || undefined}
        >
          {failure}
          {detail ? `: ${detail}` : ''}
        </span>
      )}
    </XStack>
  );
}

// Reconnect for a healthy row. Low-prominence icon (unlike the labeled strip
// CTA for broken rows) since re-authorising a working account is a rare
// maintenance action. Hidden while the row already shows an attention CTA so
// there's never two reconnect affordances on one row.
function ReconnectControl({
  account,
  name,
  onReconnect,
}: {
  account: IntegrationAccount;
  name: string;
  onReconnect: () => Promise<void>;
}) {
  const { status, run } = useActionStatus();
  if (!account.enabled || attentionSpecFor(account)) return null;

  return (
    <YStack className="items-end gap-1">
      <button
        type="button"
        title="Reconnect"
        aria-label={`Reconnect ${name}`}
        disabled={status === 'pending'}
        onClick={() => run(onReconnect)}
        className="p-1.5 rounded-md text-foreground-muted transition-colors hover:text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Icons.refresh className="size-4" />
      </button>
      {status === 'error' && (
        <p className="text-xs text-destructive">Failed to reconnect</p>
      )}
    </YStack>
  );
}

function EnabledToggleControl({
  account,
  name,
  onSetEnabled,
}: {
  account: IntegrationAccount;
  name: string;
  onSetEnabled: (enabled: boolean) => Promise<void>;
}) {
  const { status, run } = useActionStatus();

  return (
    <YStack className="items-end gap-1">
      <button
        type="button"
        title={account.enabled ? 'Pause' : 'Resume'}
        aria-label={account.enabled ? `Pause ${name}` : `Resume ${name}`}
        disabled={status === 'pending'}
        onClick={() => run(() => onSetEnabled(!account.enabled))}
        className="p-1.5 rounded-md text-foreground-muted transition-colors hover:text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {account.enabled ? (
          <Icons.pause className="size-4" />
        ) : (
          <Icons.playAlt className="size-4" />
        )}
      </button>
      {status === 'error' && (
        <p className="text-xs text-destructive">Failed to update account</p>
      )}
    </YStack>
  );
}

export function RemoveControl({
  name,
  onRemove,
}: {
  name: string;
  onRemove: () => Promise<void>;
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const { status, run } = useActionStatus();

  const confirmRemove = () => {
    setShowConfirm(false);
    run(onRemove);
  };

  return (
    <YStack className="items-end gap-1">
      <button
        type="button"
        title="Remove"
        aria-label={`Remove ${name}`}
        disabled={status === 'pending'}
        onClick={() => setShowConfirm(true)}
        className="p-1.5 rounded-md text-foreground-muted transition-colors hover:text-destructive hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Icons.trash className="size-4" />
      </button>
      {status === 'error' && (
        <p className="text-xs text-destructive">Failed to remove account</p>
      )}
      <Dialog.Root open={showConfirm} onOpenChange={setShowConfirm}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Header>
            <Dialog.Title>Remove {name}?</Dialog.Title>
            <Dialog.Description>
              This removes {name} from Littlebird. You&apos;ll need to reconnect
              it to use it again.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={confirmRemove}>
              Remove account
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </YStack>
  );
}

function AccountRow({
  account,
  providerName,
  actions,
}: {
  account: IntegrationAccount;
  providerName: string;
  actions: ReturnType<typeof useProviderAccountActions>;
}) {
  const name = accountName(account, providerName);
  // Compared against the resolved `name`, not the raw label — when the
  // identity hint is what's standing in for a missing label, showing it
  // again here would just duplicate the row's own name.
  const identityLine =
    account.identityHint &&
    account.identityHint !== name &&
    !looksLikeRawId(account.identityHint, account.id)
      ? account.identityHint
      : null;

  return (
    <YStack>
      <XStack className="items-center gap-3 px-4 py-3">
        <YStack className="min-w-0 flex-1 gap-0.5">
          <RenameControl
            account={account}
            name={name}
            onRename={(label) => actions.rename(account, label)}
          />
          {identityLine && (
            <p className="truncate text-xs text-foreground-muted">
              {identityLine}
            </p>
          )}
        </YStack>
        <StatusBadge account={account} />
        <ReconnectControl
          account={account}
          name={name}
          onReconnect={() => actions.reconnect(account)}
        />
        <EnabledToggleControl
          account={account}
          name={name}
          onSetEnabled={(enabled) => actions.setEnabled(account, enabled)}
        />
        <RemoveControl name={name} onRemove={() => actions.remove(account)} />
      </XStack>
      <AccountAttentionStrip
        account={account}
        name={name}
        onReconnect={() => actions.reconnect(account)}
        onRecheck={() => actions.recheck()}
      />
    </YStack>
  );
}

export function AccountsTab({ provider }: { provider: IntegrationProvider }) {
  const actions = useProviderAccountActions(provider);

  return (
    <YStack className="gap-4 p-6">
      <YStack className="overflow-hidden rounded-lg border border-border bg-surface">
        <YStack className="py-1">
          {provider.accounts.map((account) => (
            <AccountRow
              key={account.id}
              account={account}
              providerName={provider.name}
              actions={actions}
            />
          ))}
        </YStack>
        {actions.canAdd && (
          <button
            type="button"
            onClick={() => void actions.add()}
            className="flex w-full items-center gap-2 border-t border-border px-4 py-3 text-left text-sm text-foreground-muted transition-colors hover:bg-surface-hover hover:text-foreground"
          >
            <Icons.plus className="size-4" />
            Add account
          </button>
        )}
      </YStack>
    </YStack>
  );
}
